// Extensions data — at-risk spans for typing-time annotation, redaction map, axis-evidence

// D2L1_DRAFT — concrete demo draft, broken into spans for typing-time
// inline annotation and pre-send redact UI. Each span is either plain
// (`t` only) or at-risk (`t` + risk + replacement + inference).
//
// Trait-pair invariant (load-bearing): every distinct trait in
// CHAT_DATA.messages[id=10].inferences MUST have a span here whose
// `inference.trait` matches exactly. HardStopSheet pairs them by
// trait name (spanIdxByTrait); a
// missing pair leaves an inference orphaned in the redact UI — it
// renders in the "remaining inferences" list with no chip to undo it.
// That's a study-data corruption hazard: the participant cannot
// redact a disclosure the tool reports as still leaking.
//
// Pre-change: only "Field of study" (vaccine uptake…) and "Place of
// Birth" (grew up…) had spans; "Cultural background" inference was
// unpaired and "Public health" — the most direct field-of-study
// disclosure — was rendered as plain text in F4, F2 preview, and
// the typing-mode backdrop. Three spans now, one per inference.
export const D2L1_DRAFT = {
  spans: [
    {
      t: 'Public health',
      risk: 2,
      replacement: '[research field]',
      inference: {
        trait: 'Field of study',
        value: 'Public Health (MPH or similar)',
        confidence: 0.92,
        why: 'Naming public health while discussing your thesis reveals your field of study.',
      },
    },
    { t: '. Did my thesis on ' },
    {
      t: 'vaccine uptake in rural Appalachia',
      risk: 2,
      replacement: '[research topic]',
      inference: {
        trait: 'Cultural background',
        value: 'Appalachian regional identity',
        confidence: 0.75,
        why: 'The Appalachian research topic, linked to growing up nearby and feeling personal, suggests a regional connection but does not establish cultural identity.',
      },
    },
    { t: ' — I ' },
    {
      t: 'grew up in a small town in West Virginia',
      risk: 3,
      replacement: "grew up in the area I'm researching",
      inference: {
        trait: 'Place of Birth',
        value: 'rural West Virginia',
        confidence: 0.88,
        why: 'Saying you grew up in a small town in West Virginia reveals your childhood region, not necessarily your birthplace.',
      },
    },
    { t: ' so it felt personal.' },
  ],
};

// Per-axis evidence for F3 drill-down. Maps axis → contributing messages.
export const AXIS_EVIDENCE = {
  Location: {
    explainer:
      'Location is exposed because messages 2 and 7 named specific neighborhoods and a transit line.',
    items: [
      {
        msgId: 2,
        snippet: "farmer's market near the Blue Line, then brunch in Southbank",
        trait: 'Lives on Blue Line corridor',
        confidence: 0.82,
      },
      {
        msgId: 7,
        snippet: 'office in Midtown … 45 min each way on the train',
        trait: 'Workplace in Midtown',
        confidence: 0.88,
      },
    ],
  },
  Age: {
    explainer: 'Age narrows when life-stage cues stack up (recent grad + new to office).',
    items: [
      {
        msgId: 8,
        snippet: 'still getting used to 9-to-5 life',
        trait: 'Early-career band 24–28',
        confidence: 0.62,
      },
    ],
  },
  Sex: {
    explainer: 'Few direct signals; remains largely unknown to the reader.',
    items: [],
  },
  Occupation: {
    explainer:
      'Hybrid schedule + Midtown commute + recent masters together imply salaried knowledge work.',
    items: [
      {
        msgId: 7,
        snippet: 'office in Midtown',
        trait: 'Office-based knowledge work',
        confidence: 0.65,
      },
      {
        msgId: 6,
        snippet: 'Hybrid. 2 days in, 3 from home',
        trait: 'Hybrid employment',
        confidence: 0.55,
      },
    ],
  },
  PlaceOfBirth: {
    explainer: 'Currently soft. The pending draft would push this to high.',
    items: [
      {
        msgId: 10,
        snippet: '(draft) grew up in a small town in West Virginia',
        trait: 'Rural West Virginia origin',
        confidence: 0.88,
        isDraft: true,
      },
    ],
  },
  MaritalStatus: {
    explainer: 'No direct disclosures so far.',
    items: [],
  },
  Income: {
    explainer: 'Soft lifestyle signal — not enough on its own to localize.',
    items: [
      {
        msgId: 2,
        snippet: 'brunch in Southbank',
        trait: 'Disposable-income lifestyle',
        confidence: 0.48,
      },
    ],
  },
  EducationLevel: {
    explainer: 'A direct statement — the strongest single disclosure in this session.',
    items: [
      {
        msgId: 8,
        snippet: 'just finished my masters last year',
        trait: "Holds a master's degree",
        confidence: 0.95,
      },
    ],
  },
};
